import type { WritingsBackend } from './backend/types'
import type { TakenCard } from './types'
import { normalizeSpace } from './text'

// "Take" — the reader keeping a line (or a whole quote) from someone's writing.
//
// Three jobs live here, all DOM-level and framework-free so they're testable:
//  * reading an excerpt out of a text selection, exactly the way the server
//    will compare it (save_writing_line: html stripped, <br> as a space,
//    whitespace collapsed);
//  * marking the viewer's taken lines inside a block's (already sanitised)
//    DOM, by splitting text nodes — never by building HTML strings;
//  * a small localStorage queue for takes made while signed out, flushed into
//    the reader's thoughts once they sign in;
//  * the cards of the reader's Taken sidebar, which are loose thoughts until
//    the reader sorts them — one card at a time, never a remembered default.

/* ------------------------------------------------------------------ text */

/** One piece of a block's linear text: a text node, or a <br> read as a space. */
export interface TextSegment {
  node: Node
  start: number
  end: number
}

/**
 * A block's text in reading order, as the server sees it: text nodes
 * concatenated, each <br> counting as one space.
 */
export function linearize(root: Node): { text: string; segments: TextSegment[] } {
  const segments: TextSegment[] = []
  let text = ''
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const data = (child as Text).data
        segments.push({ node: child, start: text.length, end: text.length + data.length })
        text += data
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if ((child as Element).tagName === 'BR') {
          segments.push({ node: child, start: text.length, end: text.length + 1 })
          text += ' '
        } else {
          walk(child)
        }
      }
    })
  }
  walk(root)
  return { text, segments }
}

/**
 * Whitespace-collapsed text plus, for each of its characters, the index of
 * the raw character it came from — so a match in normalised text can be
 * mapped back onto the DOM.
 */
export function normalizeWithMap(raw: string): { norm: string; map: number[] } {
  let norm = ''
  const map: number[] = []
  let pendingSpace = -1
  for (let i = 0; i < raw.length; i++) {
    if (/\s/.test(raw[i])) {
      if (pendingSpace < 0) pendingSpace = i
      continue
    }
    if (pendingSpace >= 0 && norm.length > 0) {
      norm += ' '
      map.push(pendingSpace)
    }
    pendingSpace = -1
    norm += raw[i]
    map.push(i)
  }
  return { norm, map }
}

/** Offset in `linearize(root).text` of a DOM boundary point (container, offset). */
function boundaryOffset(root: Node, segments: TextSegment[], total: number, container: Node, offset: number): number {
  if (container !== root && !root.contains(container)) {
    return root.compareDocumentPosition(container) & Node.DOCUMENT_POSITION_PRECEDING ? 0 : total
  }
  if (container.nodeType === Node.TEXT_NODE) {
    const seg = segments.find((s) => s.node === container)
    if (seg) return seg.start + Math.min(Math.max(0, offset), seg.end - seg.start)
  }
  // An element boundary sits before childNodes[offset] (or at the element's end).
  const before = container.childNodes[offset] ?? null
  for (const seg of segments) {
    const n = seg.node
    if (before) {
      if (n === before || before.contains(n) || before.compareDocumentPosition(n) & Node.DOCUMENT_POSITION_FOLLOWING) return seg.start
    } else if (!container.contains(n) && container.compareDocumentPosition(n) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return seg.start
    }
  }
  return total
}

/**
 * The excerpt a selection covers inside one block's text root, normalised
 * the way the server compares it. Parts of the range outside `root` are
 * clamped away.
 */
export function excerptFromRange(range: Range, root: Node): string {
  const { text, segments } = linearize(root)
  const start = boundaryOffset(root, segments, text.length, range.startContainer, range.startOffset)
  const end = boundaryOffset(root, segments, text.length, range.endContainer, range.endOffset)
  return end > start ? normalizeSpace(text.slice(start, end)) : ''
}

/** The element carrying `data-take` (a block's takeable text) that holds `node`. */
export function takeRootOf(node: Node | null): HTMLElement | null {
  const el = node && (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement)
  return (el?.closest('[data-take]') as HTMLElement | null) ?? null
}

/** One block's part of a selected passage. */
export interface TakePart {
  blockId: string
  excerpt: string
  root: HTMLElement
}

/**
 * What a selection would take: each block's selected words, in reading
 * order. Only the takeable text counts — a drag that starts in the margin, or
 * runs over a caption, a label or the gap between paragraphs, still takes the
 * words it covers. A passage over several paragraphs is one part per block
 * (the server keeps a line per block). Empty when nothing takeable is selected.
 */
export function selectionTakes(range: Range, within: Element): TakePart[] {
  if (range.collapsed) return []
  const roots: HTMLElement[] = []
  const add = (el: HTMLElement | null) => {
    if (el && within.contains(el) && !roots.includes(el)) roots.push(el)
  }
  // The ends first (a selection wholly inside one block never meets intersectsNode's edge cases)…
  add(takeRootOf(range.startContainer))
  // …then every takeable block the range passes through.
  within.querySelectorAll<HTMLElement>('[data-take]').forEach((el) => {
    try {
      if (range.intersectsNode(el)) add(el)
    } catch {
      /* a detached node */
    }
  })
  add(takeRootOf(range.endContainer))
  const parts: TakePart[] = []
  for (const root of roots) {
    const excerpt = excerptFromRange(range, root)
    if (excerpt && root.dataset.take) parts.push({ blockId: root.dataset.take, excerpt, root })
  }
  // Reading order, whatever order they were found in.
  return parts.sort((a, b) => (a.root.compareDocumentPosition(b.root) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
}

/**
 * The selection inside ONE block: its block and excerpt. A triple-click that
 * runs on to the start of the next block (no text of its own selected) still
 * counts as the first; a passage over two blocks is null (see selectionTakes).
 */
export function selectionTake(range: Range, within: Element): TakePart | null {
  const parts = selectionTakes(range, within)
  return parts.length === 1 ? parts[0] : null
}

/* ------------------------------------------------------------- highlights */

/** Merged [start, end) ranges in `norm` where any of the excerpts occurs. */
export function matchRanges(norm: string, excerpts: readonly string[]): Array<[number, number]> {
  const found: Array<[number, number]> = []
  for (const raw of excerpts) {
    const e = normalizeSpace(raw)
    if (!e) continue
    let from = 0
    for (;;) {
      const i = norm.indexOf(e, from)
      if (i < 0) break
      found.push([i, i + e.length])
      from = i + e.length
    }
  }
  found.sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const merged: Array<[number, number]> = []
  for (const r of found) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}

/**
 * Wrap every occurrence of the excerpts in `root` in `<mark data-take-mark>`,
 * matching on whitespace-normalised text so a line taken across a line break
 * or an <em> still finds itself. A match spanning several text nodes gets one
 * mark per node. Returns how many ranges were marked.
 */
export function highlightExcerpts(root: Node, excerpts: readonly string[]): number {
  if (excerpts.length === 0) return 0
  const { text, segments } = linearize(root)
  const { norm, map } = normalizeWithMap(text)
  const ranges = matchRanges(norm, excerpts).map(([s, e]): [number, number] => [map[s], map[e - 1] + 1])
  if (ranges.length === 0) return 0
  const doc = root.ownerDocument ?? document
  for (const seg of segments) {
    if (seg.node.nodeType !== Node.TEXT_NODE) continue
    const local = ranges
      .filter(([s, e]) => s < seg.end && e > seg.start)
      .map(([s, e]): [number, number] => [Math.max(s, seg.start) - seg.start, Math.min(e, seg.end) - seg.start])
    if (local.length === 0) continue
    const data = (seg.node as Text).data
    const frag = doc.createDocumentFragment()
    let cursor = 0
    for (const [a, b] of local) {
      if (a > cursor) frag.appendChild(doc.createTextNode(data.slice(cursor, a)))
      const mark = doc.createElement('mark')
      mark.setAttribute('data-take-mark', '')
      mark.textContent = data.slice(a, b)
      frag.appendChild(mark)
      cursor = b
    }
    if (cursor < data.length) frag.appendChild(doc.createTextNode(data.slice(cursor)))
    ;(seg.node as Text).replaceWith(frag)
  }
  return ranges.length
}

/* ------------------------------------------------------------------ queue */

export type TakeKind = 'line' | 'quote'

/** A take made while signed out, kept in this browser until sign-in. */
export interface QueuedTake {
  writingId: string
  blockId: string
  kind: TakeKind
  /** The line (normalised); for a quote, its text, for display only. */
  excerpt: string
  pageUrl: string
  /** When it was taken (ISO), for the card's time. Absent in queues from before cards. */
  at?: string
}

export interface TakeStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem?(key: string): void
}

export const TAKE_QUEUE_KEY = 'burrow-writings-take-queue'

/** localStorage, or nothing at all when it's unavailable (private mode, blocked). */
export function browserTakeStore(): TakeStore | null {
  try {
    const ls = window.localStorage
    ls.getItem(TAKE_QUEUE_KEY)
    return ls
  } catch {
    return null
  }
}

/** Two takes are the same when they'd make the same thought. */
export function takeKey(t: Pick<QueuedTake, 'writingId' | 'blockId' | 'kind' | 'excerpt'>): string {
  return t.kind === 'quote' ? `${t.writingId}|${t.blockId}|quote` : `${t.writingId}|${t.blockId}|line|${normalizeSpace(t.excerpt)}`
}

function isQueuedTake(v: unknown): v is QueuedTake {
  const t = v as QueuedTake
  return (
    !!t &&
    typeof t.writingId === 'string' &&
    typeof t.blockId === 'string' &&
    (t.kind === 'line' || t.kind === 'quote') &&
    typeof t.excerpt === 'string' &&
    typeof t.pageUrl === 'string' &&
    (t.at === undefined || typeof t.at === 'string')
  )
}

export function readQueue(store: TakeStore | null = browserTakeStore()): QueuedTake[] {
  if (!store) return []
  try {
    const parsed: unknown = JSON.parse(store.getItem(TAKE_QUEUE_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter(isQueuedTake) : []
  } catch {
    return []
  }
}

function writeQueue(items: QueuedTake[], store: TakeStore | null): void {
  if (!store) return
  try {
    if (items.length === 0 && store.removeItem) store.removeItem(TAKE_QUEUE_KEY)
    else store.setItem(TAKE_QUEUE_KEY, JSON.stringify(items))
  } catch {
    /* storage full or blocked — the take stays on screen for this visit */
  }
}

/** Add a take to the queue (once). Returns the whole queue. */
export function queueTake(item: QueuedTake, store: TakeStore | null = browserTakeStore()): QueuedTake[] {
  const clean = { ...item, excerpt: item.kind === 'line' ? normalizeSpace(item.excerpt) : item.excerpt }
  const queue = readQueue(store)
  if (queue.some((t) => takeKey(t) === takeKey(clean))) return queue
  const next = [...queue, clean]
  writeQueue(next, store)
  return next
}

/** Drop one take from the queue. Returns the whole queue. */
export function unqueueTake(
  item: Pick<QueuedTake, 'writingId' | 'blockId' | 'kind' | 'excerpt'>,
  store: TakeStore | null = browserTakeStore(),
): QueuedTake[] {
  const next = readQueue(store).filter((t) => takeKey(t) !== takeKey(item))
  writeQueue(next, store)
  return next
}

export function queuedFor(writingId: string, store: TakeStore | null = browserTakeStore()): QueuedTake[] {
  return readQueue(store).filter((t) => t.writingId === writingId)
}

const inFlight = new Set<string>()

/** Refusals from save_writing_line / save_writing_quote that retrying can't fix. */
const PERMANENT_TAKE_ERROR = /not found|shorter line|is empty|pick a line/i

/**
 * Save this writing's queued takes into the (now signed-in) reader's
 * thoughts, loose: no collection, the reader sorts them afterwards.
 * Successful ones leave the queue; so do ones the server says no longer exist
 * (the author edited that line away). Anything else stays for next time.
 * Safe to call twice at once: the second call does nothing.
 */
export async function flushQueue(
  backend: Pick<WritingsBackend, 'saveLine' | 'saveQuote'>,
  writingId: string,
  store: TakeStore | null = browserTakeStore(),
): Promise<{ taken: number; failed: number }> {
  if (inFlight.has(writingId)) return { taken: 0, failed: 0 }
  inFlight.add(writingId)
  try {
    const mine = queuedFor(writingId, store)
    const done = new Set<string>()
    let taken = 0
    let failed = 0
    for (const t of mine) {
      try {
        if (t.kind === 'quote') await backend.saveQuote(t.writingId, t.blockId, t.pageUrl)
        else await backend.saveLine(t.writingId, t.blockId, t.excerpt, t.pageUrl)
        done.add(takeKey(t))
        taken++
      } catch (e) {
        failed++
        // The server said no for good (line edited away, too long, empty): stop retrying it.
        // Anything else (offline, a blip) stays queued for next time.
        if (e instanceof Error && PERMANENT_TAKE_ERROR.test(e.message)) done.add(takeKey(t))
      }
    }
    // Re-read: another tab may have queued more while we were saving.
    writeQueue(readQueue(store).filter((t) => !done.has(takeKey(t))), store)
    return { taken, failed }
  } finally {
    inFlight.delete(writingId)
  }
}

/* ------------------------------------------------------------ taken cards */

/** A signed-in reader's take on its way to the server, or one the server refused. */
export interface PendingTake {
  /** cardKey() of the take. */
  key: string
  kind: TakeKind
  blockId: string
  /** The line (normalised), or the quote's text. */
  text: string
  at: string
  status: 'saving' | 'error'
  error?: string
}

/** saved: a thought of theirs · saving / error: on its way, or refused · queued: kept in this browser until they join. */
export type CardStatus = 'saved' | 'saving' | 'error' | 'queued'

/** One card in the reader's Taken sidebar. */
export interface SidebarCard {
  /** Stable while the card is in one state; changes when a pending take becomes a thought. */
  key: string
  kind: TakeKind
  blockId: string
  text: string
  /** When it was taken (ISO); null when unknown. */
  at: string | null
  /** The thought it became. Only a saved card has one, and only a saved card can be sorted. */
  thoughtId: string | null
  /** The reader's collections holding it; empty = loose. */
  collectionIds: string[]
  status: CardStatus
  error?: string
}

/** One key per take, whether it's pending, queued or saved: a quote once per block, a line by its text. */
export function cardKey(kind: TakeKind, blockId: string, text: string): string {
  return kind === 'quote' ? `quote|${blockId}` : `line|${blockId}|${normalizeSpace(text)}`
}

/**
 * Everything the reader took from one writing, newest first: the server's
 * cards, takes still saving (or refused), and ones kept in this browser
 * (`queuedAs`: 'queued' signed out, 'saving' while they're being flushed after
 * sign-in). A take the server already has shows once, as its saved card.
 */
export function sidebarCards(
  saved: readonly TakenCard[],
  pending: readonly PendingTake[],
  queued: readonly QueuedTake[],
  queuedAs: CardStatus = 'queued',
): SidebarCard[] {
  const have = new Set(saved.map((c) => cardKey(c.kind, c.block_id, c.text)))
  const cards: SidebarCard[] = []
  for (const p of pending) {
    if (have.has(p.key)) continue
    have.add(p.key)
    cards.push({ key: `p:${p.key}`, kind: p.kind, blockId: p.blockId, text: p.text, at: p.at, thoughtId: null, collectionIds: [], status: p.status, error: p.error })
  }
  for (const q of queued) {
    const key = cardKey(q.kind, q.blockId, q.excerpt)
    if (have.has(key)) continue
    have.add(key)
    cards.push({ key: `q:${key}`, kind: q.kind, blockId: q.blockId, text: q.excerpt, at: q.at ?? null, thoughtId: null, collectionIds: [], status: queuedAs })
  }
  for (const c of saved) {
    // A server from before cards has no thought ids or times: such a card shows, but can't be sorted.
    cards.push({
      key: c.thought_id ? `t:${c.thought_id}` : `s:${cardKey(c.kind, c.block_id, c.text)}`,
      kind: c.kind,
      blockId: c.block_id,
      text: c.text,
      at: c.created_at || null,
      thoughtId: c.thought_id || null,
      collectionIds: c.collection_ids,
      status: 'saved',
    })
  }
  // Newest first; unknown times last. The sort is stable, so ties keep the order above.
  const time = (c: SidebarCard) => (c.at ? Date.parse(c.at) || 0 : -Infinity)
  return cards.sort((a, b) => time(b) - time(a))
}

/**
 * A taken card's source line: the writing and its author, and, for words
 * from an attributed pull quote, whoever said them first, the writing being
 * where the reader found them (Niki, 10/6): "Steven Johnson · via Why We
 * Save… · Niki". The servers' takes (save_writing_quote / save_writing_line,
 * v2.4) write the thought's source title the same way.
 */
export function takenSourceLine(writingTitle: string, author: string, saidBy?: string): string {
  const where = [writingTitle || 'Untitled', author].filter(Boolean).join(' · ')
  return saidBy ? `${saidBy} · via ${where}` : where
}

/** A taken card in no collection: the default, and never assumed otherwise. */
export const LOOSE_LABEL = 'Loose thoughts'

/** What a card's sorting chip says: "Loose thoughts", a collection, or "Taste +1" when it's in several. */
export function sortLabel(collectionIds: readonly string[], names: ReadonlyMap<string, string>): string {
  if (collectionIds.length === 0) return LOOSE_LABEL
  const first = collectionIds.map((id) => names.get(id)).find(Boolean) ?? 'A collection'
  return collectionIds.length > 1 ? `${first} +${collectionIds.length - 1}` : first
}

/** "1 line, 2 quotes" (zero parts left out). */
export function takeBreakdown(lines: number, quotes: number): string {
  const parts: string[] = []
  if (lines) parts.push(`${lines} line${lines === 1 ? '' : 's'}`)
  if (quotes) parts.push(`${quotes} quote${quotes === 1 ? '' : 's'}`)
  return parts.join(', ')
}
